import {
  forwardRef,
  Fragment,
  HTMLAttributes,
  ReactNode,
  ElementType,
  isValidElement
} from 'react';

import { cn } from '@/lib/utils/cn';

export type T_TypographyVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'p';

export interface I_TypographyProps extends HTMLAttributes<HTMLElement> {
  variant: T_TypographyVariant;
  children: ReactNode;
  icon?: ReactNode;
  iconPosition?: 'before' | 'after';
  as?: ElementType;
  className?: string;
}

// Default typography styles
const typographyStyles: Record<T_TypographyVariant, string> = {
  h1: 'text-4xl md:text-5xl font-extrabold leading-10 md:leading-12 tracking-tight',
  h2: 'text-3xl font-semibold leading-9 tracking-tight',
  h3: 'text-2xl font-semibold leading-8 tracking-tight',
  h4: 'text-xl font-semibold leading-7 tracking-tight',
  p: 'text-base font-normal leading-6'
};

// Default element mapping
const defaultElements: Record<T_TypographyVariant, ElementType> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  p: 'p'
};

export const Typography = forwardRef<HTMLElement, I_TypographyProps>(
  (
    {
      variant,
      children,
      icon,
      iconPosition = 'before',
      as,
      className,
      ...props
    },
    ref
  ) => {
    const Element = as || defaultElements[variant];

    const baseStyles = typographyStyles[variant];

    const iconElement = icon && (
      <span className="inline-flex items-center">{icon}</span>
    );

    const normalizedChildren = Array.isArray(children)
      ? children.map((child, index) =>
          isValidElement(child) && child.key != null ? (
            child
          ) : (
            <Fragment key={index}>{child}</Fragment>
          )
        )
      : children;

    const content = icon ? (
      <>
        {iconPosition === 'before' && iconElement}

        <span
          className={cn(
            icon && iconPosition === 'before' && 'ml-2.5',
            icon && iconPosition === 'after' && 'mr-2.5'
          )}
        >
          {normalizedChildren}
        </span>

        {iconPosition === 'after' && iconElement}
      </>
    ) : (
      <>{normalizedChildren}</>
    );

    return (
      <Element
        ref={ref}
        className={cn(
          'flex item-center text-foreground',
          baseStyles,
          className
        )}
        {...props}
      >
        {content}
      </Element>
    );
  }
);

Typography.displayName = 'Typography';

// Convenience components for each variant
export const H1 = forwardRef<
  HTMLHeadingElement,
  Omit<I_TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="h1" {...props} />);
H1.displayName = 'H1';

export const H2 = forwardRef<
  HTMLHeadingElement,
  Omit<I_TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="h2" {...props} />);
H2.displayName = 'H2';

export const H3 = forwardRef<
  HTMLHeadingElement,
  Omit<I_TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="h3" {...props} />);
H3.displayName = 'H3';

export const H4 = forwardRef<
  HTMLHeadingElement,
  Omit<I_TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="h4" {...props} />);
H4.displayName = 'H4';

export const P = forwardRef<
  HTMLParagraphElement,
  Omit<I_TypographyProps, 'variant'>
>((props, ref) => <Typography ref={ref} variant="p" {...props} />);
P.displayName = 'P';
